import type { FC, ReactNode } from 'react';
import { SvgIcon } from 'root/widgets/svg-icon';
import { HUIModal, HUIModalProps } from 'root/widgets/common-components/hui-modal';
import { Typography } from 'root/common/atom-elements/typography';
import classnames from 'classnames';
import kebabCase from 'lodash/kebabCase';
import parse, { type DOMNode, HTMLReactParserOptions } from 'html-react-parser';

import { BadgeVariant, ExplanationProps, VERIFIED_BADGE_EVENTS } from 'root/widgets/verified-badge/types';
import { HIVE_UI_CORE_URL, MARKETPLACE } from 'root/widgets/constants';
import { extractDataAutomationId } from 'root/widgets/utils/automation';
import { emit } from 'root/widgets/events';

// Move the helper functions outside the component
const extractTextFromNode = (node): string => {
  if (!node) return '';

  if (typeof node === 'string') return node;

  if (node.type === 'text') {
    return node.data || '';
  }

  if (node.type === 'tag' && node.children) {
    return node.children.map((child) => extractTextFromNode(child)).join('');
  }

  if (node.children && Array.isArray(node.children)) {
    return node.children.map((child) => extractTextFromNode(child)).join('');
  }

  return '';
};

const parseHtmlWithClickHandler = (htmlContent: string, handleReportAgent: () => void) => {
  try {
    const options: HTMLReactParserOptions = {
      replace: (domNode: DOMNode) => {
        if (
          !domNode ||
          typeof domNode !== 'object' ||
          !('type' in domNode) ||
          domNode.type !== 'tag' ||
          !('name' in domNode) ||
          domNode.name !== 'a'
        ) {
          return domNode;
        }

        const linkText = extractTextFromNode(domNode).toLowerCase().trim();

        // Check for report-related text patterns
        const reportPatterns = ['report it here'];
        const foundPattern = reportPatterns.find((pattern) => linkText.includes(pattern));

        if (foundPattern) {
          const attribs = domNode.attribs ?? {};
          // Extract href for navigation while removing conflicts
          const { href, onclick, ...cleanAttribs } = attribs;

          // If there's a valid href, use an anchor element
          if (href && href !== '#') {
            return (
              <a
                {...cleanAttribs}
                href={href}
                onClick={() => {
                  handleReportAgent();
                }}
                style={{
                  cursor: 'pointer',
                  textDecoration: 'underline',
                  ...(typeof attribs.style === 'object' ? attribs.style : {}),
                }}
              >
                {linkText}
              </a>
            );
          }

          // If there's no valid href, use a button element for accessibility
          return (
            <button
              {...cleanAttribs}
              onClick={(e) => {
                handleReportAgent();
                e.preventDefault();
                e.stopPropagation();
              }}
              style={{
                cursor: 'pointer',
                textDecoration: 'underline',
                background: 'none',
                border: 'none',
                padding: 0,
                font: 'inherit',
                color: 'inherit',
                ...(typeof attribs.style === 'object' ? attribs.style : {}),
              }}
              type="button"
            >
              {linkText}
            </button>
          );
        }

        return domNode;
      },
    };

    return parse(htmlContent, options);
  } catch (error) {
    console.warn('Error parsing HTML with click handler:', error);
    return parse(htmlContent);
  }
};

interface BadgeModalProps extends HUIModalProps {
  isOpen: boolean;
  onClose: () => void;
  modalTitle: string;
  variant: BadgeVariant;
  marketplace: MARKETPLACE;
  icon: string;
  text: string;
  explanation: ExplanationProps;
  note?: string | ReactNode;
  trackingData?: {
    segmentData?: {
      [key: string]: string | number | boolean;
    };
  };
}

const BadgeModal: FC<BadgeModalProps> = ({
  isOpen,
  onClose,
  modalTitle,
  variant,
  marketplace,
  icon,
  text,
  explanation,
  note,
  trackingData,
  size,
  className,
}) => {
  const handleReportAgent = () => {
    if (trackingData?.segmentData) {
      emit(VERIFIED_BADGE_EVENTS.REPORT_VERIFIED_AGENT, { ...trackingData.segmentData });
    }
  };

  return (
    <HUIModal
      onHide={onClose}
      show={isOpen}
      className={classnames('verified-badge-modal', className)}
      size={size}
      {...extractDataAutomationId({
        dataAutomationId: `${variant}-badge-dlg`,
      })}
    >
      <HUIModal.Header
        closeButton
        className="verified-badge-modal__header"
        {...extractDataAutomationId({
          dataAutomationId: `${variant}-badge-dlg-title`,
        })}
      >
        {modalTitle}
      </HUIModal.Header>
      <HUIModal.Body
        className="verified-badge-modal__body"
        {...extractDataAutomationId({
          dataAutomationId: `${variant}-badge-dlg-pnl`,
        })}
      >
        <div
          className={classnames('verified-badge-modal__badge', `verified-badge-modal__badge--${variant}`, marketplace)}
          {...extractDataAutomationId({
            dataAutomationId: `${variant}-badge-dlg-img`,
          })}
        >
          <SvgIcon src={`${HIVE_UI_CORE_URL}icons/svgs/${icon}.svg`} width={56} height={56} title={text} />
        </div>

        <Typography
          element="h3"
          variant="title-xs"
          className="verified-badge-modal__explanation-title"
          metadata={{
            dataAutomationId: `${variant}-badge-dlg-explanation-title`,
          }}
        >
          {explanation.title}
        </Typography>

        <Typography
          className="verified-badge-modal__explanation-description"
          metadata={{
            dataAutomationId: `${variant}-badge-dlg-explanation-description`,
          }}
        >
          {explanation.description}
        </Typography>
        <Typography
          element="p"
          variant="label-m"
          className="verified-badge-modal__details-subtitle"
          metadata={{
            dataAutomationId: `${variant}-badge-dlg-details-subtitle`,
          }}
        >
          {explanation.subTitle}
        </Typography>
        <div
          className="verified-badge-modal__details-list"
          {...extractDataAutomationId({
            dataAutomationId: `${variant}-badge-dlg-details-lst`,
          })}
        >
          {explanation.details.map((detail, index) => (
            <div
              key={`detail-${kebabCase(detail.title)}`}
              className="verified-badge-modal__detail-item"
              {...extractDataAutomationId({
                dataAutomationId: `${variant}-badge-dlg-detail-item-${index}-text`,
              })}
            >
              <div className="verified-badge-modal__detail-icon-wrapper">
                <SvgIcon
                  src={`${HIVE_UI_CORE_URL}icons/svgs/${detail.iconName ?? 'check-radio-o'}.svg`}
                  width={24}
                  height={24}
                  shouldUseImage
                />
              </div>
              <div className="verified-badge-modal__detail-wrapper">
                {detail.title && (
                  <Typography variant="label-m" className="verified-badge-modal__detail-title">
                    {detail.title}
                  </Typography>
                )}
                <Typography className="verified-badge-modal__detail-description">
                  {typeof detail.description === 'string' ? parse(detail.description) : detail.description}
                </Typography>
              </div>
            </div>
          ))}
        </div>
        {note && (
          <Typography
            variant="body-xs"
            className="verified-badge-modal__note"
            metadata={{
              dataAutomationId: `${variant}-badge-dlg-note-text`,
            }}
          >
            {typeof note === 'string' ? parseHtmlWithClickHandler(note, handleReportAgent) : note}
          </Typography>
        )}
      </HUIModal.Body>
    </HUIModal>
  );
};

export default BadgeModal;
